/**
 * 認証関連のAPIエンドポイント
 */

import apiClient from '../client';
import type { LoginResponse, User } from '../../../types';

interface SignupRequest {
  user: {
    user_id: string;
    password: string;
    name: string;
    email?: string;
    role?: string; // 新規店舗登録時は'admin'を設定
  };
  store: {
    name: string;
    code?: string; // 自動生成されるため任意
    address: string;
    phone: string;
    postal_code?: string;
    prefecture?: string;
    city?: string;
    address_line1?: string;
    address_line2?: string;
    contact_email?: string;
    contact_phone?: string;
    business_hours?: Record<string, any>;
    manager_name?: string;
  };
}

export interface SignupResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: {
    id: number;
    store_id: number;
    user_id: string;
    name: string;
    role: string;
    is_active: boolean;
    created_at: string;
  };
  store: {
    id: number;
    name: string;
    code: string;
    address: string;
    phone: string;
    is_active: boolean;
    postal_code?: string;
    prefecture?: string;
    city?: string;
    contract_type_id?: number;
    created_at: string;
  };
  contract?: {
    id: number;
    contract_number: string;
    contract_type_id: number;
    start_date: string;
    end_date: string;
    monthly_fee: string;
    discount_rate: string;
    final_monthly_fee?: string;
    status: string;
    auto_renewal: number;
    created_at: string;
  };
  message: string;
}

export const authApi = {
  /**
   * ログイン
   *
   * 注意: パスワードはAPIクライアントによって自動的にマスキングされ、
   * ログには [MASKED] として記録されます。
   */
  login: async (userId: string, password: string): Promise<LoginResponse> => {
    return apiClient.post<LoginResponse>('/auth/login', {
      userId,
      password
    });
  },

  /**
   * トークンリフレッシュ
   *
   * 注意: リフレッシュトークンはAPIクライアントによって自動的にマスキングされ、
   * ログには [MASKED] として記録されます。
   */
  refresh: async (refreshToken: string): Promise<LoginResponse> => {
    return apiClient.post<LoginResponse>('/auth/refresh', {
      refreshToken
    });
  },

  /**
   * ログアウト
   */
  logout: async (): Promise<void> => {
    return apiClient.post<void>('/auth/logout');
  },

  /**
   * ユーザー一覧取得
   */
  getUsers: async (): Promise<User[]> => {
    return apiClient.get<User[]>('/users');
  },

  /**
   * サインアップ（新規店舗登録）
   *
   * 注意: パスワードはAPIクライアントによって自動的にマスキングされ、
   * ログには [MASKED] として記録されます。
   *
   * 新規店舗登録時に作成される最初のユーザーは管理者権限(role: 'admin')を持ちます。
   */
  signup: async (signupData: SignupRequest): Promise<SignupResponse> => {
    return apiClient.post<SignupResponse>('/auth/signup', signupData);
  }
};
